import type { AttributeFilters } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { DeviceAttributesFilter } from 'components/DeviceAttributesFilter/DeviceAttributesFilter';
import type { FacetedFilterValue } from 'components/FacetedFilter/FacetedFilter';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { setGlobalAttributeFilters } from 'components/GlobalState/commands/setGlobalAttributeFilters';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useState } from 'react';
import type { FC } from 'react';

interface Props {
  onClose: () => void;
}

export const HeaderAttributeFilterModal: FC<Props> = (props) => {
  const {
    app: {
      header: { attributeFilters: attributeFiltersMsg },
    },
  } = useLocale();
  const [value, setValue] = useState<FacetedFilterValue>({});
  const globalCtxProps = useGlobalStateContextSelectors({
    attributeFilters: prop('attributeFilters'),
    dispatch: prop('dispatch'),
  });

  const handleApply = () => {
    globalCtxProps.dispatch(setGlobalAttributeFilters, {
      attributeFilters: Object.entries(value).reduce<AttributeFilters>(
        (acc, [key, val]) => {
          if (val.length > 0) {
            acc[key] = val;
          }

          return acc;
        },
        {},
      ),
    });

    props.onClose();
  };

  useEffect(() => {
    setValue(globalCtxProps.attributeFilters);
  }, [globalCtxProps.attributeFilters]);

  return (
    <OverlayModal onClose={props.onClose}>
      <OverlayModalHeader>
        <h4>{attributeFiltersMsg.attributeFilters}</h4>
      </OverlayModalHeader>
      <OverlayModalBody>
        <DeviceAttributesFilter
          value={value}
          onChange={setValue}
          globalAttributes={false}
        />
      </OverlayModalBody>
      <OverlayModalFooter>
        <Button
          className='mr-2'
          variant='secondary'
          type='button'
          onClick={() => {
            props.onClose();
          }}
        >
          {attributeFiltersMsg.cancel}
        </Button>
        <Button
          type='button'
          variant='primary'
          onClick={handleApply}
          disabled={
            !(
              Object.values(value).length === 0 ||
              Object.values(value).some((v) => v.length > 0)
            )
          }
        >
          {attributeFiltersMsg.apply}
        </Button>
      </OverlayModalFooter>
    </OverlayModal>
  );
};
